import {
  ACCESSIBILITY_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { MENU_ITEM_SELECTOR } from '../../../../utils/spds/constants/menu';
import { ROLE_LISTBOX } from '../../../../utils/spds/constants/aria';
import { fn, hash } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import Footer from './footer.gjs';
import Header from './header.gjs';
import NoResults from './no-results.gjs';
import Super from '../../core/base.gjs';
import VariableLayout from '../../layout/variable.gjs';
import styles from './base.module.scss';

const List = <template>
  <VariableLayout
    @column={{@column}}
    @gap="4px"
    @id={{@id}}
    @registerComponent={{@registerListComponent}}
    @unregisterComponent={{@unregisterListComponent}}
    aria-labelledby={{@ariaLabelledBy}}
    class="spds-menu-list"
    data-test-spds-menu-list
    role={{@role}}
  >
    {{yield}}

    {{#if @showNoResults}}
      <NoResults @text={{@noResultsText}} />
    {{/if}}
  </VariableLayout>
</template>;

const METADATA = Metadata.define({
  description: `
Simple, easy to use menu component. This is the primitive menu shell: it handles positioning and
renders a scrollable list of the items yielded to it, plus an optional no-results state and an
optional footer. For an in-menu search field that filters items as the user types, use
\`Spds::Menu::Filterable\`, which extends this component.

### Positioning

Providing the \`@anchorElement\` argument as a DOM element that is used to compute the \`top\` property for the menu. This can be
further modified by providing the \`@anchorOffset\` argument, which adjusts the distance between the top of the menu and the bottom of
the anchor element. Left/right alignment is determined by the width of the menu and the horizontal positioning of the anchor. If the anchor
position plus the menu width extends beyond the right edge of the viewport, the menu will automatically align to the right side of the anchor.
Otherwise, the menu defaults to aligning to the left side of the anchor. In either case, the position of the non-aligned side will be determined
in one of two ways:

* Default behavior causes the menu to flex its width to fit the container of the anchor element
* If the \`@fitContent\` argument is provided, the menu will restrict its width to the required space to fit its largest content

### No results

When the consumer indicates the menu is empty via \`@noItemsDisplayed\`, a no-results message (customizable via \`@noResultsText\`)
is shown in place of the list.

### Footer

An optional \`:footer\` named block renders pinned below the scrollable list, outside the scroll region. Consumers that need
persistent controls (for example, apply/clear actions) can render them here.
  `,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_LISTBOX }, 'ariaLabelledBy']),
    pick(COMPONENT_STATE_ARGS, ['open', { name: 'animated', default: true }]),
    pick(CONTAINER_ARGS, ['fitContent', { name: 'column', default: true }]),
    pick(MENU_ARGS, ['anchorElement', { name: 'anchorOffset', default: 4 }]),
    pick(INPUT_MENU_ARGS, ['noItemsDisplayed', 'noResultsText']),
  ],
  name: 'Menu Base',
});

export default class SpdsCoreMenuBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu';
  classesToMap = ['animated', 'open', 'side'];
  propsToMap = ['top'];
  styles = styles;

  @tracked side = 'left';
  @tracked listComponent;
  @tracked top;

  get anchorBoundingRect() {
    return this.args.anchorElement?.getBoundingClientRect();
  }

  get anchor() {
    return this.args.anchorElement;
  }

  get anchorDistance() {
    return this.args.anchorOffset ?? this.constructor.metadata.getDefault('anchorOffset');
  }

  get animated() {
    return this.argOrDefault('animated');
  }

  get baseElementRegistered() {
    return !!this.baseElement;
  }

  get canBePositioned() {
    return this.hasAnchor && this.baseElementRegistered;
  }

  get column() {
    return this.argOrDefault('column');
  }

  get hasAnchor() {
    return !!this.args.anchorElement;
  }

  get listId() {
    return this.composeSubcomponentId('list');
  }

  get parentBoundingRect() {
    return this.args.anchorElement?.parentElement?.getBoundingClientRect();
  }

  get itemElements() {
    if (!this.baseElement) return [];

    return Array.from(this.listComponent?.baseElement?.querySelectorAll(MENU_ITEM_SELECTOR) ?? []);
  }

  get role() {
    return this.argOrDefault('role');
  }

  get showNoResults() {
    return !!this.args.noItemsDisplayed;
  }

  setMenuPosition = modifier(() => {
    if (!this.canBePositioned) return;

    let anchorBottom = this.anchorBoundingRect?.bottom;
    let menuRight = this.anchorBoundingRect?.x + this.baseElement?.getBoundingClientRect()?.width;
    let parentTop = this.parentBoundingRect?.top || 0;
    let top = anchorBottom - parentTop + this.anchorDistance;

    this.side = menuRight > window.innerWidth ? 'right' : 'left';
    this.top = `${top}px`;
  });

  <template>
    <FlexColumn
      @absolute={{true}}
      @fitContentWidth={{@fitContent}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu
      id={{this.id}}
      style={{this.customProperties}}
      {{this.setupComponent}}
      {{this.setMenuPosition}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Body=(component
            List
            ariaLabelledBy=@ariaLabelledBy
            column=@column
            id=this.listId
            noResultsText=@noResultsText
            registerListComponent=(fn this.registerComponent "listComponent")
            role=@role
            showNoResults=this.showNoResults
            unregisterListComponent=(fn this.unregisterComponent "listComponent")
          )
          Footer=Footer
          Header=Header
          listId=this.listId
        )
      }}
    </FlexColumn>
  </template>
}

export const SPDS_CORE_MENU_BASE_METADATA = SpdsCoreMenuBase.metadata;
